import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

interface MotionSensorCardProps {
  deviceId: string;
  deviceName: string;
  lastData?: any;
  config?: any;
}

export const MotionSensorCard: React.FC<MotionSensorCardProps> = ({
  deviceName,
  lastData,
  config,
}) => {
  const { t } = useTranslation();
  const motionDetected = lastData?.motion_detected || false;
  const batteryLevel = lastData?.battery_v || 0;
  const lastMotion = lastData?.last_motion_at;

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Ionicons name="walk-outline" size={24} color="#ec4899" />
        <Text style={styles.deviceName}>{deviceName}</Text>
      </View>

      {/* Motion Status */}
      <View style={styles.statusContainer}>
        <View
          style={[styles.statusBadge, motionDetected ? styles.activeBadge : styles.inactiveBadge]}
        >
          <Ionicons name={motionDetected ? 'warning' : 'checkmark-circle'} size={32} color="#fff" />
          <Text style={styles.statusText}>
            {motionDetected ? t('deviceDetail.motionDetected') : t('deviceDetail.noMotion')}
          </Text>
        </View>
      </View>

      {/* Metrics */}
      <View style={styles.metricsContainer}>
        {batteryLevel > 0 && (
          <View style={styles.metricRow}>
            <Ionicons name="battery-half" size={20} color="#64748b" />
            <Text style={styles.metricLabel}>{t('deviceDetail.battery')}</Text>
            <Text style={styles.metricValue}>{batteryLevel.toFixed(2)} V</Text>
          </View>
        )}

        {lastMotion && (
          <View style={styles.metricRow}>
            <Ionicons name="time-outline" size={20} color="#64748b" />
            <Text style={styles.metricLabel}>{t('deviceDetail.lastMotion')}</Text>
            <Text style={styles.metricValue}>{new Date(lastMotion).toLocaleTimeString()}</Text>
          </View>
        )}
      </View>

      {/* Last Update */}
      {lastData?.timestamp && (
        <Text style={styles.timestamp}>
          {t('deviceDetail.lastUpdate')}: {new Date(lastData.timestamp).toLocaleString()}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  deviceName: {
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 12,
    color: '#1f2937',
  },
  statusContainer: {
    alignItems: 'center',
    marginVertical: 24,
  },
  statusBadge: {
    paddingHorizontal: 32,
    paddingVertical: 20,
    borderRadius: 16,
    alignItems: 'center',
    minWidth: 200,
  },
  activeBadge: {
    backgroundColor: '#ef4444',
  },
  inactiveBadge: {
    backgroundColor: '#10b981',
  },
  statusText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
    marginTop: 8,
  },
  metricsContainer: {
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    marginTop: 8,
  },
  metricRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
  },
  metricLabel: {
    fontSize: 14,
    color: '#6b7280',
    marginLeft: 8,
    flex: 1,
  },
  metricValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1f2937',
  },
  timestamp: {
    fontSize: 12,
    color: '#9ca3af',
    textAlign: 'center',
    marginTop: 12,
  },
});
